import { useRef } from "react";

type SwipeHandlers = {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
};

export function useSwipeCarousel(opts: {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  onSwipeStart?: () => void;
  onSwipeEnd?: () => void;
  thresholdPx?: number;   // min horizontal distance to count as swipe
  restraintPx?: number;   // max vertical movement allowed
}) {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeStart,
    onSwipeEnd,
    thresholdPx = 40,
    restraintPx = 60,
  } = opts;

  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  const activePointerId = useRef<number | null>(null);
  const swiping = useRef(false);

  const reset = () => {
    startX.current = null;
    startY.current = null;
    activePointerId.current = null;
    swiping.current = false;
    onSwipeEnd?.();
  };

  const handlers: SwipeHandlers = {
    onPointerDown: (e) => {
      // Only primary touch/pen/mouse
      if (activePointerId.current !== null) return;

      activePointerId.current = e.pointerId;
      startX.current = e.clientX;
      startY.current = e.clientY;
      swiping.current = true;
      onSwipeStart?.();

      // Capture pointer so we still get move/up even if finger leaves element
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    },

    onPointerMove: (e) => {
      if (!swiping.current) return;
      if (activePointerId.current !== e.pointerId) return;
      if (startX.current === null || startY.current === null) return;

      const dx = e.clientX - startX.current;
      const dy = e.clientY - startY.current;

      // If user is scrolling vertically, ignore swipe
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
        swiping.current = false;
        onSwipeEnd?.();
        return;
      }

      // We purposely do NOT preventDefault here; instead we use CSS touch-action
      // to allow vertical scrolling and still detect horizontal swipes.
    },

    onPointerUp: (e) => {
      if (activePointerId.current !== e.pointerId) return;
      if (startX.current === null || startY.current === null) return;

      const dx = e.clientX - startX.current;
      const dy = e.clientY - startY.current;

      // Reject if too much vertical drift (user scrolled)
      if (Math.abs(dy) > restraintPx) return reset();

      if (dx <= -thresholdPx) onSwipeLeft();
      else if (dx >= thresholdPx) onSwipeRight();

      reset();
    },

    onPointerCancel: () => reset(),
  };

  return handlers;
}
